/**
 * `/v1/imports` — the request shapes. The row cap is not here: Q-P31 puts it at parse (U74).
 */
import { z } from "zod";
import { DATE_ORDERS, IMPORT_ACTIONS, IMPORT_ERROR_KINDS, IMPORT_STATUSES, importMappingSchema, importRowErrorSchema, } from "../import.js";
/**
 * 200 MB, binary megabytes. `designs/Screen · Spreadsheet Import.dc.html:248` reads
 * `CSV or XLSX · up to 50,000 rows · 200 MB`; it is not in `screens.md`.
 */
export const IMPORT_MAX_BYTES = 200 * 1024 * 1024;
export const DEFAULT_IMPORT_PAGE_SIZE = 50;
export const MAX_IMPORT_PAGE_SIZE = 200;
/** `shopify` is in `IMPORT_SOURCES` for Phase 8; no door here writes it. */
export const IMPORT_UPLOAD_SOURCES = ["csv", "xlsx"];
const SHA256_MESSAGE = "contentSha256 must be the file's SHA-256 as 64 hexadecimal digits.";
export const createImportSchema = z
    .object({
    filename: z
        .string("filename is required.")
        .min(1, "filename is required.")
        .max(255, "filename is at most 255 characters."),
    bytes: z
        .number("bytes must be a whole number of bytes, zero or more.")
        .int("bytes must be a whole number of bytes, zero or more.")
        .min(0, "bytes must be a whole number of bytes, zero or more."),
    contentSha256: z.string(SHA256_MESSAGE).regex(/^[0-9a-f]{64}$/, SHA256_MESSAGE),
    idempotencyKey: z
        .string("idempotencyKey is required.")
        .min(1, "idempotencyKey is required.")
        .max(255, "idempotencyKey is at most 255 characters."),
    source: z.enum(IMPORT_UPLOAD_SOURCES, "source must be one of: csv, xlsx."),
})
    .describe("POST /v1/imports — returns a presigned PUT.");
export const importIdSchema = z.object({ id: z.uuid("id must be a UUID.") });
export const importListQuerySchema = z
    .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce
        .number("limit must be a whole number of rows.")
        .int("limit must be a whole number of rows.")
        .min(1, "limit must be at least 1.")
        .max(MAX_IMPORT_PAGE_SIZE, `limit is at most ${MAX_IMPORT_PAGE_SIZE}.`)
        .default(DEFAULT_IMPORT_PAGE_SIZE),
})
    .describe("GET /v1/imports — a keyset page, newest first.");
/** The review screen's page, and the one `row_no` cursor's page size. */
export const IMPORT_ROWS_PAGE_SIZE = 500;
export const importRowsQuerySchema = z
    .object({
    action: z
        .enum(IMPORT_ACTIONS, `action must be one of: ${IMPORT_ACTIONS.join(", ")}.`)
        .optional(),
    kind: z
        .enum(IMPORT_ERROR_KINDS, `kind must be one of: ${IMPORT_ERROR_KINDS.join(", ")}.`)
        .optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce
        .number("limit must be a whole number of rows.")
        .int("limit must be a whole number of rows.")
        .min(1, "limit must be at least 1.")
        .max(IMPORT_ROWS_PAGE_SIZE, `limit is at most ${IMPORT_ROWS_PAGE_SIZE}.`)
        .default(IMPORT_ROWS_PAGE_SIZE),
})
    .describe("GET /v1/imports/{id}/rows — a keyset page over row_no, ascending.");
export const importReportQuerySchema = z
    .object({
    rows: z
        .enum(["failed"], "rows is failed. Leave it out for the rows that did not validate.")
        .optional(),
})
    .describe("GET /v1/imports/{id}/report — the error CSV.");
export const confirmImportMappingSchema = z
    .object({
    mapping: importMappingSchema.pick({ columns: true }),
    dateOrder: z
        .enum(DATE_ORDERS, `dateOrder must be one of: ${DATE_ORDERS.join(", ")}.`)
        .nullable()
        .optional(),
    saveAs: z
        .string("saveAs must be a template name.")
        .trim()
        .min(1, "saveAs must be a template name, or left out.")
        .max(120, "saveAs is at most 120 characters.")
        .optional(),
})
    .describe("POST /v1/imports/{id}/mapping — confirms the columns and starts validation.");
export const applyImportSchema = z
    .object({
    workspaceId: z.uuid("workspaceId must be the id of one of this organisation's workspaces."),
    limit: z
        .number("limit must be a whole number of rows, one or more.")
        .int("limit must be a whole number of rows, one or more.")
        .min(1, "limit must be a whole number of rows, one or more.")
        .optional(),
})
    .describe("POST /v1/imports/{id}/apply — creates the reviewed rows as drafts and updates the rest.");
export const importViewSchema = z.object({
    id: z.uuid(),
    filename: z.string(),
    source: z.string(),
    status: z.enum(IMPORT_STATUSES),
    bytes: z.number().nullable(),
    contentSha256: z.string().nullable(),
    mapping: z.unknown(),
    templateId: z.string().nullable(),
    stats: z.unknown(),
    checkpoint: z.unknown(),
    error: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** `dateOrder` is read as null when `import_templates.date_order` holds a word `DATE_ORDERS` lacks. */
export const importTemplateRefSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    dateOrder: z.enum(DATE_ORDERS).nullable(),
});
export const importOverLimitSchema = z.object({
    allowance: z.number().int(),
    published: z.number().int(),
    newProducts: z.number().int(),
    shortBy: z.number().int(),
});
/** `GET /v1/imports/{id}`: the view with the template read in place of its bare id (Drift §U76). */
export const importDetailSchema = importViewSchema.omit({ templateId: true }).extend({
    template: importTemplateRefSchema.nullable(),
    overLimit: importOverLimitSchema.nullable(),
});
export const importUploadSchema = z.object({
    import: importViewSchema,
    upload: z
        .object({
        url: z.string(),
        method: z.literal("PUT"),
        expiresInSeconds: z.number(),
    })
        .nullable(),
    maxBytes: z.number(),
});
export const importPageSchema = z.object({
    imports: z.array(importViewSchema),
    nextCursor: z.string().nullable(),
});
export const importRowViewSchema = z.object({
    rowNo: z.number().int(),
    raw: z.record(z.string(), z.string()),
    errors: z.array(importRowErrorSchema),
    action: z.enum(IMPORT_ACTIONS).nullable(),
});
export const importRowsPageSchema = z.object({
    rows: z.array(importRowViewSchema),
    nextCursor: z.string().nullable(),
});
export const importReportSchema = z.object({ filename: z.string(), csv: z.string() });
/**
 * `imports.stats`, read loosely: parse, validate and apply each write their own keys, so
 * which are present depends on how far the import got.
 *
 * `create` and `update` are validate's plan. `created` and `updated` are what apply did.
 * They differ by `applyFailed` and `notApplied`, and only apply's pair counts a catalogue row.
 */
export const importStatsSchema = z.object({
    rows: z.number().optional(),
    columns: z.number().optional(),
    templateApplied: z.boolean().optional(),
    warnings: z.array(z.string()).optional(),
    notImported: z.record(z.string(), z.string()).optional(),
    ready: z.number().optional(),
    errors: z.number().optional(),
    byKind: z.record(z.string(), z.number()).optional(),
    create: z.number().optional(),
    update: z.number().optional(),
    skip: z.number().optional(),
    applied: z.number().optional(),
    applyFailed: z.number().optional(),
    created: z.number().optional(),
    updated: z.number().optional(),
    notApplied: z.number().optional(),
});
/** `imports.checkpoint`: `rowNo` is parse's, `validatedRowNo` validate's, `applied` apply's row_no watermark. */
export const importCheckpointSchema = z.object({
    rowNo: z.number().optional(),
    validatedRowNo: z.number().optional(),
    applied: z.number().optional(),
    chunks: z.number().optional(),
    apply: z.object({ limit: z.number().nullable().optional() }).optional(),
});
//# sourceMappingURL=imports.js.map